import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParallax } from 'react-scroll-parallax';
import { Link } from 'react-router-dom';
import './ServicePages.css';

const ComputerVision = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const headerParallax = useParallax({
    translateY: [0, 50],
    opacity: [1, 0.5],
    scale: [1, 0.9],
    easing: 'easeInQuad',
    startScroll: 0,
    endScroll: 600
  });

  // Animation variants for staggered animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12
      }
    }
  };

  const scaleVariants = {
    hover: {
      scale: 1.05,
      boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.2)",
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 15
      }
    }
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0, scale: 0.9 },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12
      }
    },
    hover: {
      scale: 1.05,
      boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.3)",
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 15
      }
    }
  };

  return (
    <div className="service-page">
      {/* Hero Section */}
      <section className="service-hero" ref={headerParallax.ref}>
        <div className="service-hero-overlay"></div>
        <div className="service-hero-content">
          <h1>Computer Vision</h1>
          <p>Advanced visual recognition and processing solutions for your business</p>
        </div>
      </section>

      {/* Overview Section */}
      <motion.section 
        className="service-overview"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={containerVariants}
      >
        <motion.h2 variants={itemVariants}>What is Computer Vision?</motion.h2>
        <motion.p variants={itemVariants}>
          Computer Vision is a field of artificial intelligence that enables computers to interpret and understand the visual world. 
          Using digital images from cameras and videos and deep learning models, machines can accurately identify and classify objects 
          and react to what they "see." At Hyperion AI, we develop advanced computer vision solutions that help businesses automate 
          visual inspection, enhance security, and gain valuable insights from visual data.
        </motion.p>
      </motion.section>

      {/* Use Cases Section */}
      <motion.section 
        className="service-use-cases"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={containerVariants}
      >
        <motion.h2 variants={itemVariants}>Use Cases</motion.h2>
        <motion.div 
          className="use-cases-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <motion.div 
            className="use-case-card computer-vision-card-1"
            variants={cardVariants}
            whileHover="hover"
          >
            <div className="use-case-icon">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3>Quality Control</h3>
            <p>Automated visual inspection systems that detect defects and ensure product quality in manufacturing.</p>
          </motion.div>
          
          <motion.div 
            className="use-case-card computer-vision-card-2"
            variants={cardVariants}
            whileHover="hover"
          >
            <div className="use-case-icon">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h3>Security & Surveillance</h3>
            <p>Advanced facial recognition, object detection, and anomaly detection for enhanced security systems.</p>
          </motion.div>
          
          <motion.div 
            className="use-case-card computer-vision-card-3"
            variants={cardVariants}
            whileHover="hover"
          >
            <div className="use-case-icon">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h3>Retail Analytics</h3>
            <p>Customer behavior analysis, inventory management, and checkout-free shopping experiences.</p>
          </motion.div>
          
          <motion.div 
            className="use-case-card computer-vision-card-4"
            variants={cardVariants}
            whileHover="hover"
          >
            <div className="use-case-icon">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
            </div>
            <h3>Medical Imaging</h3>
            <p>Diagnostic assistance, anomaly detection, and treatment planning through medical image analysis.</p>
          </motion.div>
        </motion.div>
      </motion.section>

      {/* Technology Section */}
      <motion.section 
        className="service-technology"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={containerVariants}
      >
        <motion.h2 variants={itemVariants}>Our Technology</motion.h2>
        <motion.p variants={itemVariants}>
          At Hyperion AI, we leverage cutting-edge computer vision technologies to deliver solutions that see and understand the world like humans do, but with the speed and precision of machines.
        </motion.p>
        <motion.div className="technology-features" variants={containerVariants}>
          <motion.div className="technology-feature" variants={itemVariants}>
            <h3>Deep Learning Models</h3>
            <p>Custom-trained convolutional neural networks (CNNs) for image classification, object detection, and segmentation.</p>
          </motion.div>
          <motion.div className="technology-feature" variants={itemVariants}>
            <h3>Real-time Processing</h3>
            <p>Edge computing solutions that enable real-time video analysis and decision-making with minimal latency.</p>
          </motion.div>
          <motion.div className="technology-feature" variants={itemVariants}>
            <h3>Multi-modal Integration</h3>
            <p>Combining visual data with other sensors and data sources for comprehensive environmental understanding.</p>
          </motion.div>
        </motion.div>
      </motion.section>

      {/* CTA Section */}
      <section className="service-cta">
        <div className="service-cta-content">
          <h2>Ready to Implement Computer Vision in Your Business?</h2>
          <p>Contact our team to discuss how our computer vision solutions can address your specific needs.</p>
          <Link to="/contact-us" className="cta-button">Get Started</Link>
        </div>
      </section>
    </div>
  );
};

export default ComputerVision;
